import {LitElement, PropertyValues, html, nothing} from "lit";
import {customElement, property, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";

import {citationNotesStyles} from "./CitationNotes.styles.js";
import {citationSourceNote} from "../../../main/data/model/index.js";

import "../../common/controls-inputs/Button.js";
import "../../common/controls-inputs/IconButton.js";

import Data from "../../../main/data/index.js";
import Project from "../../../main/project.js";

@customElement("pn-citation-notes")
export class CitationNotes extends LitElement {
	@property() editable: boolean;
	@property() notes:    citationSourceNote[] = [];
	@property() title:    string;

	@state() canEditSourceNoteLabels = Project.hasSourceNoteLabelsEditingAccess(Data.app.project);
	@state() _notes: citationSourceNote[] = [];

	static styles = [citationNotesStyles];

	willUpdate(changedProperties: PropertyValues<this>) {
		if (changedProperties.has("notes"))
			this._notes = this.notes;
	}

	onClickDeleteNote = (index: number) => {
		const notesCopy = Array.from(this._notes);
		notesCopy.splice(index, 1);
		this._notes = notesCopy;
	};

	onClickAddNote = () => {
		this._notes = [
			...this._notes,
			{
				label:    this.getNewNoteLabel(),
				note:     "",
				position: this.getNewNotePosition()
			}
		];
	};

	/**
	 * Determine the label of a new source note based on the existing source
	 * note labels, and will be "Source Note X". If there is no other note
	 * labeled "Source Note", X is left out. Otherwise, X is the next smallest
	 * unused number among the other labels.
	 **/
	getNewNoteLabel(): string {
		// Search through notes for any label that starts with "Source Note"
		const SLICE_LENGTH = "Source Note ".length;
		let labels = this._notes.map(note => note.label);
		labels = labels.filter(label => {
			if (!label.startsWith("Source Note"))
				return false;

			// See if the text after Source Note is numbers only
			const rem = label.slice(SLICE_LENGTH);
			return !Number.isNaN(rem);
		});

		// Sort the labels in order of the number following "Source Note"
		labels.sort((a, b) => {
			const remA = a.slice(SLICE_LENGTH);
			const remB = b.slice(SLICE_LENGTH);

			return Number(remA) - Number(remB);
		});

		// Find smallest number not yet used in the labels
		let newNoteNumber = 0;
		for (const label of labels) {
			const number = Number(label.slice(SLICE_LENGTH));

			if (number < newNoteNumber)
				break;
			if (number === newNoteNumber) {
				if (number === 0)
					// Special case: 'Source Note 2' comes after 'Source Note'
					newNoteNumber = number + 2;
				else
					newNoteNumber = number + 1;
			}

		}

		const newLabel = (newNoteNumber > 0)
			? `Source Note ${newNoteNumber}`
			: "Source Note";

		return newLabel;
	}

	/**
	 * Determine the next highest position number based on the positions of
	 * existing notes. Because it's only used here for repeat()'s id, it doesn't
	 * matter if it's not the actual position. The position is redetermined
	 * elsewhere before being saved.
	 **/
	getNewNotePosition(): number {
		const positions = this._notes.map(note => note.position);
		return Math.max(...positions) + 1;
	}

	handleChange(event: InputEvent, originalLabel: string) {
		const target = event.target as HTMLInputElement;
		if (!target)
			return;

		// Don't allow an empty field, reset it
		if (target.value === "") {
			target.value = originalLabel;
			target.parentElement?.setAttribute("data-value", originalLabel);
		}
	};

	handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === "Enter" || event.key === "Escape" || event.key === "Esc")
			(event.target as HTMLElement).blur();
	};

	handleInput = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		const value = target?.value;
		target?.parentElement?.setAttribute("data-value", value);
	};

	renderSourceNote(note: citationSourceNote, index: number) {
		const numNotes = this._notes.length;

		return html`
			<div class="citation-source-note">
				<div class="citation-source-note-label">
					<label class="note-label" data-value="${note.label}">
						<input
							aria-label="Source notes label"
							autoComplete="off"
							class="${classMap({
								"label-input": true,
								editable:      this.editable && this.canEditSourceNoteLabels
							})}"
							@change="${(event: InputEvent) => this.handleChange(event, note.label)}"
							@input="${this.handleInput}"
							@keydown="${this.handleKeyDown}"
							maxLength="128"
							name="label"
							?readonly="${(!this.editable || !this.canEditSourceNoteLabels) ?? nothing}"
							size="1"
							value="${note.label}"
						/>
					</label>
					<pn-icon-button
						class="${classMap({
							"citation-note-delete": true,
							hidden:                 (!this.editable || !this.canEditSourceNoteLabels) || numNotes === 1
						})}"
						icon="trash"
						.onClick="${() => this.onClickDeleteNote(index)}"
						tooltipText="Delete source note"
					></pn-icon-button>
				</div>
				<textarea
					class="custom-scroll-bar-light"
					maxLength="2048"
					placeholder="Enter notes on how you found your source, its validity, or anything else."
					?readonly="${!this.editable}"
					.value=${note.note}
				></textarea>
			</div>
		`;
	}

	render() {
		let heading = this.title;
		if (this.editable && heading === "")
			heading = "Add Source Notes";

		return html`
			<div id="citation-notes">
				${(heading !== "")
					? html`<h3>${heading}</h3>`
					: nothing
				}

				<div id="citation-notes-list">
					${repeat(this._notes, (note) => note.position, (note, i) => this.renderSourceNote(note, i))}

					${(this.editable && this.canEditSourceNoteLabels)
						? html`<pn-button
							id="btn-citation-add-note"
							.onClick="${this.onClickAddNote}"
							renderType="link"
						>
							+ Add another text field
						</pn-button>`
						: nothing
					}
				</div>
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-citation-notes": CitationNotes;
	}
}
